import { useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Item } from '../../models/Item'
import { Transaction } from '../../models/Transaction'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { CurrencyField } from '../common/fields/CurrencyField'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { SelectedPhoto } from '../workflows/PhotoPicker'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    item: Item
    onSave: (item: Item, file?: File) => void
    onClose: () => void
    onDelete?: () => void
    isLoading?: boolean
    enableAllFields?: boolean
}

export const GiftTypePage = ({
    item,
    onSave,
    onClose,
    onDelete,
    isLoading,
    enableAllFields = true,
}: Props) => {
    const [scratchGiftCertificate, setScratchGiftCertificate] = useState<Item>(item)
    const owner = PSApplication.owner()
    const [guardrail, setGuardrail] = useState<
        undefined | 'singleCapacity' | 'availability' | 'location' | 'price'
    >(undefined)
    const [selectedPhoto, setSelectedPhoto] = useState<SelectedPhoto | null>(
        item.image?.uri ? { type: 'url', url: item.image.url ?? '' } : null
    )

    const isNextEnabled = useMemo(() => {
        return scratchGiftCertificate.name !== ''
    }, [scratchGiftCertificate.name])

    return (
        <WorkflowPage
            title="Gift Certificate"
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        onDelete !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {onDelete !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={onDelete}
                            isLoading={isLoading}
                        >
                            Delete gift certificate
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={() => {
                            if (Number.isNaN(scratchGiftCertificate.deposit)) {
                                setGuardrail('price')
                            }
                            else {
                                onSave(
                                    scratchGiftCertificate,
                                    selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                                )
                            }
                        }}
                        disabled={isLoading || !isNextEnabled}
                        isLoading={isLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            onClose={onClose}
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    {enableAllFields && (
                        <WorkflowPage.PhotoPicker
                            onSelectPhoto={(photoFile) => {
                                setSelectedPhoto({ type: 'file', file: photoFile })
                            }}
                            onClearPhoto={() => setSelectedPhoto(null)}
                            selectedPhoto={selectedPhoto}
                        />
                    )}

                    <WorkflowPage.IconSection
                        title="Name (required)"
                        icon={<Icon icon="gift.large" background="round" />}
                    >
                        <TextField
                            isFullWidth
                            size="medium"
                            placeholder="Enter name"
                            value={scratchGiftCertificate.name}
                            onClear={() => setScratchGiftCertificate((i) => i?.clone({ name: '' }))}
                            onChange={(e) => {
                                setScratchGiftCertificate((i) => i?.clone({ name: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Price"
                        icon={<Icon icon="dollar.large" background="round" />}
                    >
                        <CurrencyField
                            isFullWidth
                            size="medium"
                            placeholder="0.00"
                            value={scratchGiftCertificate.deposit}
                            min={0}
                            step={0.01}
                            icon={'dollar9.large'}
                            onClear={() => {
                                setScratchGiftCertificate((i) => i?.clone({ deposit: undefined }))
                            }}
                            onChange={(e) => {
                                setScratchGiftCertificate((i) => i?.clone({ deposit: e }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowDiscount && (
                        <WorkflowPage.IconSection
                            title="Discount rate"
                            icon={<Icon icon="discount.large" background="round" />}
                        >
                            <Select
                                size="medium"
                                selectedOptionID={scratchGiftCertificate.discount?.toString()}
                                options={Transaction.discountRateStrings().map((option) => ({
                                    id: option.id ?? '',
                                    title: option.label ?? '',
                                }))}
                                onSelectOption={(option) => {
                                    setScratchGiftCertificate((i) =>
                                        i?.clone({ discount: option.id })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}
                    {owner.allowSalesTax && (
                        <WorkflowPage.IconSection
                            title="Allow sales tax"
                            description="Automatically calculate & apply sales tax"
                            icon={<Icon icon="percentage2.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchGiftCertificate.allowSalesTax}
                                    onChange={(e) =>
                                        setScratchGiftCertificate((i) =>
                                            i?.clone({ allow_salestax: e })
                                        )
                                    }
                                />
                            }
                        />
                    )}
                    <WorkflowPage.IconSection
                        title="Description"
                        icon={<Icon icon="note.large" background="round" />}
                    >
                        <TextArea
                            size="medium"
                            placeholder="Enter description"
                            isAutoResizing
                            value={scratchGiftCertificate.memo}
                            onClear={() => setScratchGiftCertificate((i) => i?.clone({ memo: '' }))}
                            onChange={(e) => {
                                setScratchGiftCertificate((i) => i?.clone({ memo: e.target.value }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Details">
                    <WorkflowPage.IconSection title="Expires after" icon="calendar.large">
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                            <Chip
                                title="No expiration"
                                isSelected={!scratchGiftCertificate.expires}
                                onClick={() =>
                                    setScratchGiftCertificate((i) =>
                                        i?.clone({ expires: undefined })
                                    )
                                }
                                size="small"
                            />
                            <Chip
                                title="1 year"
                                isSelected={scratchGiftCertificate.expires === 360}
                                onClick={() =>
                                    setScratchGiftCertificate((i) => i?.clone({ expires: 360 }))
                                }
                                size="small"
                            />
                            <Chip
                                title="2 years"
                                isSelected={scratchGiftCertificate.expires === 720}
                                onClick={() =>
                                    setScratchGiftCertificate((i) => i?.clone({ expires: 720 }))
                                }
                                size="small"
                            />
                            <Chip
                                title="5 years"
                                isSelected={scratchGiftCertificate.expires === 1800}
                                onClick={() =>
                                    setScratchGiftCertificate((i) => i?.clone({ expires: 1800 }))
                                }
                                size="small"
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    {owner.allowWidget && (
                        <WorkflowPage.IconSection
                            title="Show online"
                            description="Allow clients to purchase this service online"
                            icon={<Icon icon="computerDollar.large" background="round" />}
                            hasHoverEffect
                            secondaryView={
                                <Checkbox
                                    isChecked={scratchGiftCertificate.isPublic}
                                    onChange={(e) => {
                                        setScratchGiftCertificate((i) => i?.clone({ is_public: e }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Attachments">
                    <WorkflowPage.IconSection
                        title="Attach a contract"
                        description="This contract will be signed when a client purchases this service"
                        icon={<Icon icon="pen.large" background="round" />}
                    >
                        <ContractSearchableSelect
                            selectedContract={scratchGiftCertificate.contract}
                            onSelectContract={(contract) => {
                                setScratchGiftCertificate((i) => i?.clone({ contract }))
                            }}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Attach a form"
                        description="This form will be completed when a client purchases this service"
                        icon={<Icon icon="field.large" background="round" />}
                    >
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={scratchGiftCertificate.form}
                            onSelectForm={(form) => {
                                setScratchGiftCertificate((i) => i?.clone({ form }))
                            }}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <ConfirmationModal
                    title="Would you like to continue without adding a price?"
                    isOpen={guardrail === 'price'}
                    onClose={() => setGuardrail(undefined)}
                    onConfirm={() => {
                        onSave(
                            scratchGiftCertificate,
                            selectedPhoto?.type === 'file' ? selectedPhoto.file : undefined
                        )
                        setGuardrail(undefined)
                    }}
                >
                    <Paragraph className="text-gray-600">
                        If you don’t add a price this service will be shown as a price of “TBD” and
                        allow you to edit the price on a service by service basis.
                    </Paragraph>
                    <Paragraph className="mt-4 text-gray-600">
                        This is great if you don’t necessarily know the price of the service before
                        booking it.
                    </Paragraph>
                </ConfirmationModal>
            </div>
        </WorkflowPage>
    )
}
